import {
  booleanAttribute,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { PecAnimations } from '../../animations/pec-animations';
import { IconName } from '@fortawesome/free-regular-svg-icons';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';

export enum DateTimeEnum {
  DATE = 'DATE',
  TIME = 'TIME',
  DATETIME = 'DATETIME',
}

@Component({
  selector: 'sigecom-date-time-picker',
  animations: [PecAnimations.showOptions],
  styleUrls: ['./date-time-picker.component.scss'],
  host: { class: 'block' },
  template: `
    <sigecom-input-container
      [label]="label"
      [isRequired]="isRequired"
      [isDisabled]="isDisabled"
    >
      <div class="flex gap-2 items-center relative">
        <div *ngIf="showDate" class="relative w-full">
          <fa-icon
            [size]="sizeIcon"
            [icon]="icon || 'calendar'"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
          ></fa-icon>
          <input
            type="date"
            sigecom-input
            class="w-full pr-5"
            [value]="dateValue"
            (input)="onDateChange($event)"
            (change)="onDateChange($event)"
            (blur)="onTouched?.()"
            [disabled]="isDisabled"
          />
        </div>

        <div *ngIf="showTime" class="relative w-full">
          <fa-icon
            [size]="sizeIcon"
            [icon]="icon || 'clock'"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
          ></fa-icon>
          <input
            type="time"
            sigecom-input
            class="w-full pr-10"
            [ngClass]="{ 'pl-10': showDate }"
            [value]="timeValue"
            (input)="onTimeChange($event)"
            (change)="onTimeChange($event)"
            (blur)="onTouched?.()"
            [step]="timeStep"
            [disabled]="isDisabled"
          />
        </div>
      </div>
    </sigecom-input-container>

    <sigecom-error *ngIf="error" [error]="error" [disabled]="isDisabled" />
  `,
})
export class DateTimePickerComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input({ required: true }) label!: string;
  @Input({ required: true }) type!: DateTimeEnum;
  @Input() error: string | null = null;
  @Input({ transform: booleanAttribute }) isRequired = false;
  @Input() timeStep = 900;
  @Input({ transform: booleanAttribute }) isDisabled = false;
  @Input() icon?: IconName;
  @Input() sizeIcon: SizeProp = '1x';

  @Input({ transform: booleanAttribute })
  set required(value: boolean) {
    this.isRequired = value;
  }
  get required(): boolean {
    return this.isRequired;
  }

  @Output() changeValue = new EventEmitter<string | null>();

  dateValue = '';
  timeValue = '';

  private selectedValue: Date | null = null;

  onTouched?: () => void;
  private onChange?: (value: string | null) => void;

  private ngControl = inject(NgControl, { optional: true, self: true });
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get showDate(): boolean {
    return this.type === DateTimeEnum.DATE || this.type === DateTimeEnum.DATETIME;
  }

  get showTime(): boolean {
    return this.type === DateTimeEnum.TIME || this.type === DateTimeEnum.DATETIME;
  }

  ngOnInit(): void {
    if (this.ngControl?.control?.hasValidator(Validators.required)) {
      this.isRequired = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['type'] && !changes['type'].firstChange) {
      this.updateDisplayValues();
      this.propagateChange();
    }
  }

  writeValue(value: string | Date | null): void {
    if (!value) {
      this.clearValues();
      this.cdr.markForCheck();
      return;
    }

    let date: Date;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        this.clearValues();
        this.cdr.markForCheck();
        return;
      }

      // DATE: YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const [year, month, day] = trimmed.split('-').map(Number);
        date = new Date(year, month - 1, day);
      }
      // TIME: HH:mm or HH:mm:ss
      else if (/^\d{2}:\d{2}(:\d{2})?$/.test(trimmed)) {
        const [hours, minutes, seconds = '0'] = trimmed.split(':');
        date = new Date();
        date.setHours(+hours, +minutes, +seconds, 0);
      }
      // DATETIME / ISO
      else {
        date = new Date(trimmed);
      }
    } else {
      date = value;
    }

    if (isNaN(date.getTime())) {
      this.clearValues();
      this.cdr.markForCheck();
      return;
    }

    this.selectedValue = date;
    this.updateDisplayValues();
    this.cdr.markForCheck();
  }

  onDateChange(event: Event): void {
    const dateStr = (event.target as HTMLInputElement).value;
    this.dateValue = dateStr;

    if (!dateStr) {
      this.selectedValue = null;
      this.propagateChange();
      this.cdr.markForCheck();
      return;
    }

    const [year, month, day] = dateStr.split('-').map(Number);
    const newDate = this.selectedValue ? new Date(this.selectedValue) : new Date();

    newDate.setFullYear(year, month - 1, day);

    if (this.type === DateTimeEnum.DATE) {
      newDate.setHours(0, 0, 0, 0);
    }

    this.selectedValue = newDate;
    this.propagateChange();
    this.cdr.markForCheck();
  }

  onTimeChange(event: Event): void {
    const timeStr = (event.target as HTMLInputElement).value;
    this.timeValue = timeStr;

    if (!timeStr) {
      if (this.type === DateTimeEnum.TIME) {
        this.selectedValue = null;
      }
      this.propagateChange();
      this.cdr.markForCheck();
      return;
    }

    const [hours, minutes] = timeStr.split(':').map(Number);

    if (!this.selectedValue) {
      this.selectedValue = new Date();
    }

    this.selectedValue.setHours(hours, minutes, 0, 0);
    this.propagateChange();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  private clearValues(): void {
    this.dateValue = '';
    this.timeValue = '';
    this.selectedValue = null;
  }

  private propagateChange(): void {
    if (!this.selectedValue) {
      this.onChange?.(null);
      this.changeValue.emit(null);
      return;
    }

    let formattedValue: string;

    switch (this.type) {
      case DateTimeEnum.DATE:
        formattedValue = this.formatDate(this.selectedValue);
        break;
      case DateTimeEnum.TIME:
        formattedValue = this.formatTime(this.selectedValue);
        break;
      case DateTimeEnum.DATETIME:
        if (!this.dateValue || !this.timeValue) {
          this.onChange?.(null);
          this.changeValue.emit(null);
          return;
        }
        const datePart = this.formatDate(this.selectedValue);
        const timePart = this.formatTime(this.selectedValue);
        formattedValue = `${datePart}T${timePart}`;
        break;
      default:
        formattedValue = this.selectedValue.toISOString();
        break;
    }

    this.onChange?.(formattedValue);
    this.changeValue.emit(formattedValue);
  }

  private updateDisplayValues(): void {
    if (!this.selectedValue) {
      this.dateValue = '';
      this.timeValue = '';
      return;
    }

    if (this.showDate) {
      this.dateValue = this.formatDate(this.selectedValue);
    } else {
      this.dateValue = '';
    }

    if (this.showTime) {
      this.timeValue = this.formatTime(this.selectedValue);
    } else {
      this.timeValue = '';
    }
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private formatTime(date: Date): string {
    const h = date.getHours().toString().padStart(2, '0');
    const min = date.getMinutes().toString().padStart(2, '0');
    return `${h}:${min}`;
  }
}
